/**
 * 会社の色（1色）から、画面で使う濃淡をつくる。
 *
 * **画面に色を直接書かないための仕組みです**（docs/01 20-5・docs/06 1-1）。
 * 受け取るのは会社ごとの設定（`GET /org/branding` の `color_seed`）の1色だけで、
 * ボタンの色・押したときの色・文字にできる濃さ・薄い背景は、ここで計算して作ります。
 *
 * 大事なのは **「どんな色を設定されても字が読めること」** です（docs/06 6-1）。
 * 薄い黄色を設定されても、白い文字を載せて読めなくなる、ということが起きないように、
 *  - **色の面（ボタン）そのものを、字が読める濃さまで寄せる**（`--brand`）。
 *    白と濃い墨のどちらを載せても足りない中間の明るさの色（灰色など）があるためで、
 *    「載せる文字を選ぶ」だけでは読める濃さに届きません
 *  - その面の上に載せる文字は、白と濃い墨のうち読める方を選ぶ（`--brand-on`）
 *  - 薄い色の背景に置く「色の文字」は、読める濃さになるまで暗くした色を使う（`--brand-strong`）
 * という3つの当て方をしています。
 */

/** 光の三原色の組（0〜255）。 */
export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** 白地の上で文字として読める最低の明るさの差（WCAG AA の通常文字）。 */
export const MIN_TEXT_CONTRAST = 4.5;

/**
 * 「文字ではないが、見えていないと困るもの」の最低の差（WCAG AA の非文字）。
 * いま選んでいるところの枠（`--brand-ring`）と、ボタンの輪郭（`--brand-edge`）に使います。
 */
export const MIN_EDGE_CONTRAST = 3;

/** 濃い墨（中立色の `--ink` と同じ値）。色の上に白を載せられないときの文字色。 */
const INK: Rgb = { r: 0x1a, g: 0x1b, b: 0x22 };
const WHITE: Rgb = { r: 0xff, g: 0xff, b: 0xff };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

/** `#RRGGBB` を読み取る。形が違えば null（＝設定が読めない扱い）。 */
export function parseHexColor(value: string): Rgb | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(value.trim());
  if (!match) return null;
  const n = Number.parseInt(match[1] as string, 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

/** 三原色の組を `#RRGGBB` に戻す。 */
export function toHexColor({ r, g, b }: Rgb): string {
  const part = (v: number): string =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

/**
 * 画面に出す形（`#RRGGBB`）に丸めた色。
 *
 * 計算の途中は小数を持っていますが、画面に出るのは丸めたあとの色です。
 * **確かめるのは、実際に画面へ出る方の色**でなければ意味がありません
 * （丸めで明るさの差がわずかに減り、基準を割ることがあります）。
 */
function rounded(color: Rgb): Rgb {
  const part = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
  return { r: part(color.r), g: part(color.g), b: part(color.b) };
}

/** 2色を混ぜる（`ratio` は混ぜる側の割合・0〜1）。 */
export function mixColor(base: Rgb, other: Rgb, ratio: number): Rgb {
  const t = Math.max(0, Math.min(1, ratio));
  return {
    r: base.r + (other.r - base.r) * t,
    g: base.g + (other.g - base.g) * t,
    b: base.b + (other.b - base.b) * t,
  };
}

/** 色の明るさ（0＝黒・1＝白）。WCAG の相対輝度。 */
export function relativeLuminance({ r, g, b }: Rgb): number {
  const channel = (value: number): number => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** 2色の明るさの差（1〜21）。大きいほど読みやすい。 */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** その色の上に載せる文字の色（白か濃い墨か、読める方）。 */
export function readableTextOn(background: Rgb): Rgb {
  return contrastRatio(background, WHITE) >= contrastRatio(background, INK) ? WHITE : INK;
}

/**
 * その背景に置いても読める濃さまで暗くした色（既定の背景は白地）。
 * 1割ずつ黒を混ぜていき、明るさの差が足りたところで止める（最後は黒）。
 *
 * 背景を渡せるようにしてあるのは、**選んでいる項目のように「薄く色の付いた面」の上**にも
 * この色を置くためです。白地だけで確かめると、薄い面の上では足りないことがあります。
 */
export function darkenUntilReadable(
  seed: Rgb,
  minContrast = MIN_TEXT_CONTRAST,
  background: Rgb = WHITE,
): Rgb {
  const shownBackground = rounded(background);
  for (let step = 0; step <= 10; step += 1) {
    const candidate = mixColor(seed, BLACK, step / 10);
    // 確かめるのは、画面に出る形（丸めたあと）の色
    if (contrastRatio(rounded(candidate), shownBackground) >= minContrast) return candidate;
  }
  return BLACK;
}

/**
 * 色の面（ボタンなど）を、**字が読める濃さまで寄せた色**。
 *
 * なぜ要るか: 白と濃い墨の「読める方を選ぶ」だけでは足りない色があります。
 * 中間の明るさ（灰色や、くすんだ色）は、白を載せても墨を載せても明るさの差が 4.5 に届きません。
 * 会社の色は設定から来る＝どんな色でも入りうるので、**面の側を寄せて**必ず読めるようにします。
 *
 * 寄せる向きは、**いま近い方**に合わせます（白を載せるなら暗く、墨を載せるなら明るく）。
 * 寄せ幅は 5% ずつで、ほとんどの色はそのままか 1〜2 段で収まります。
 *
 * 返すのは**画面に出る形（丸めたあと）の色**です。丸める前の色で確かめると、
 * 丸めたときに明るさの差がわずかに減って基準を割ることがあるためです。
 */
export function readableSurface(seed: Rgb, minContrast = MIN_TEXT_CONTRAST): Rgb {
  /** 画面に出る形（丸めたあと）で、字が読めるか。 */
  const readable = (color: Rgb): boolean => {
    const shown = rounded(color);
    return contrastRatio(shown, readableTextOn(shown)) >= minContrast;
  };
  if (readable(seed)) return rounded(seed);
  // 白の方が近ければ暗く寄せる（白を載せる）。そうでなければ明るく寄せる（墨を載せる）
  const towardBlack = contrastRatio(seed, WHITE) >= contrastRatio(seed, INK);
  const target = towardBlack ? BLACK : WHITE;
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mixColor(seed, target, step / 20);
    if (readable(candidate)) return rounded(candidate);
  }
  return target;
}

/** 画面に差し込む色の一覧（CSS の変数名 → 値）。 */
export type BrandPalette = Record<string, string>;

/**
 * 会社の色から、画面で使う色の一式をつくる。
 * 設定が読めない・形が違うときは null を返す（＝中立の灰色のまま使う）。
 */
export function brandPalette(colorSeed: string): BrandPalette | null {
  const seed = parseHexColor(colorSeed);
  if (!seed) return null;
  // 面の側を、字が読める濃さまで寄せてから使う（中間の明るさの色への備え）
  const surface = readableSurface(seed);
  const on = readableTextOn(surface);
  // 選んでいる項目の背景。「色の文字」はこの面の上にも置くので、濃さの確認にも使う
  const soft = mixColor(seed, WHITE, 0.92);
  // 薄い色の面や白地に置く「色の文字」。いちばん明るい置き場（薄い色の面）で確かめる
  const strong = darkenUntilReadable(seed, MIN_TEXT_CONTRAST, soft);
  return {
    '--brand': toHexColor(surface),
    // 押したとき・カーソルを載せたときは少し暗く
    '--brand-hover': toHexColor(mixColor(surface, BLACK, 0.12)),
    // 薄い色の面や白地に置く「色の文字」（リンク・選んでいる項目の字）
    '--brand-strong': toHexColor(strong),
    // 選んでいる項目の背景・そっと色を付けたい帯
    '--brand-soft': toHexColor(soft),
    // 目印（バッジ）の背景
    '--brand-soft-strong': toHexColor(mixColor(seed, WHITE, 0.86)),
    // 色の上に載せる文字
    '--brand-on': toHexColor(on),
    // いま選んでいるところの枠。**透かしにしません。**
    // 半透明だと白地に溶けて実質見えなくなり（実測 1.72:1）、キーボードでお使いの方が
    // 「いまどこを指しているか」を見失います（issue #177 の2番）。
    // 文字として読める濃さまで暗くした色をそのまま使うので、白地でも薄い色の面でも必ず見えます
    '--brand-ring': toHexColor(strong),
    // 色の面（ボタン）の輪郭。とても明るい色を設定されたときに、
    // ボタンが白い背景に溶けて輪郭を失うのを防ぎます（issue #184 L-7）。
    // 足りている色は寄せないので、ふつうの会社の色では面と同じ色＝輪郭は目立ちません
    '--brand-edge': toHexColor(darkenUntilReadable(seed, MIN_EDGE_CONTRAST, WHITE)),
  };
}

/** そのしつらえで使う変数名の一覧（差し込みを取り消すときに使う）。 */
export const BRAND_VARIABLE_NAMES: readonly string[] = [
  '--brand',
  '--brand-hover',
  '--brand-strong',
  '--brand-soft',
  '--brand-soft-strong',
  '--brand-on',
  '--brand-ring',
  '--brand-edge',
];

/**
 * 画面の土台（`<html>`）に色を差し込む。
 * 設定が読めなければ何もしない＝`styles.css` に書いてある中立の灰色のまま（docs/06 1-1）。
 *
 * @returns 差し込めたら true
 */
export function applyBrandPalette(element: HTMLElement, colorSeed: string): boolean {
  const palette = brandPalette(colorSeed);
  if (!palette) {
    clearBrandPalette(element);
    return false;
  }
  for (const [name, value] of Object.entries(palette)) {
    element.style.setProperty(name, value);
  }
  return true;
}

/** 差し込んだ色を取り消す（ログアウトしたとき。次の会社の色が混ざらないように）。 */
export function clearBrandPalette(element: HTMLElement): void {
  for (const name of BRAND_VARIABLE_NAMES) {
    element.style.removeProperty(name);
  }
}
